import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getTableCellErrorState } from '@/components/ui-v2/inputs/table/hooks/get-table-cell-error-state';
import { TablePercentageDisplay } from '@/components/ui-v2/inputs/table/table-percentage-display';
import { TablePercentageInput } from '@/components/ui-v2/inputs/table/table-percentage-input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useCellValidationRuleError } from '@/hooks/use-cell-validation-rule-error';
import { useIsCellEditing } from '@/hooks/use-is-cell-editing';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { useUser } from '@/hooks/use-user';
import {
  DealItem,
  DealItemWithVersions,
} from '@/lib/graphql/generated/graphql';
import {
  cn,
  formatDecimal,
  getCurrentValue,
  getDeltaSign,
  getDeltaValue,
  getMyLastValue,
  hasPreviousVersion,
} from '@/lib/utils';
import { useSetColumnSort } from '@/states/column-sort.state';
import { evolutionInsightsState } from '@/states/evolution-insights.state';
import { kpiComparisonState } from '@/states/kpi-comparison.state';
import { KPIComparison } from '@/types/kpi-comparison';
import { Row } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { InfoIcon, TrendingDown, TrendingUp } from 'lucide-react';

import { ExtendedColumnDef } from '../table-products';
import { SortIcon } from './sort';
import { TableCellWithRecentlyModified } from './table-cell-with-recently-modified';

interface ColumnProps {
  handleCellClick: (id: string, field: keyof DealItem) => void;
  handleCellEdit: <K extends keyof DealItem>(
    id: string | undefined,
    field: K,
    value: DealItem[K],
    isLeafRow?: boolean
  ) => Promise<void>;
  handleKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  handleBlur: React.FocusEventHandler<HTMLInputElement>;
}

interface ChooseDiscountCellContentProps {
  row: Row<DealItemWithVersions>;
  handleCellClick: ColumnProps['handleCellClick'];
  handleCellEdit: ColumnProps['handleCellEdit'];
  handleKeyDown: ColumnProps['handleKeyDown'];
  handleBlur: ColumnProps['handleBlur'];
}

function ChooseDiscountCellContent({
  row,
  handleCellClick,
  handleCellEdit,
  handleKeyDown,
  handleBlur,
}: ChooseDiscountCellContentProps) {
  const { t } = useTranslation();
  const user = useUser();
  const kpiComparison = useAtomValue(kpiComparisonState);
  const evolutionInsights = useAtomValue(evolutionInsightsState);
  const isOfferLocked = useIsOfferLocked();
  const isEditing = useIsCellEditing(row, 'chooseDiscount');
  const isParent = row.subRows.length > 1;

  const getValue = () => {
    const value = row.original.activeVersion.chooseDiscount?.toFixed(2);
    if (typeof value === 'undefined' || value === null) return '—';
    return parseFloat(value);
  };

  const getParentValue = () => {
    const subRowsValues: number[] = [];
    row.subRows.forEach((subRow) => {
      const value = subRow.original.activeVersion.chooseDiscount?.toFixed(2);
      if (typeof value === 'undefined' || value === null) return;
      subRowsValues.push(parseFloat(value));
    });
    if (subRowsValues.length === 0) return '—';
    const min = Math.min(...subRowsValues);
    const max = Math.max(...subRowsValues);
    return min === max
      ? `${formatDecimal(max)}%`
      : `${formatDecimal(min)}% - ${formatDecimal(max)}%`;
  };

  const getParentComparedValue = () => {
    if (
      kpiComparison !== KPIComparison.DISCOUNT_HIGHER &&
      kpiComparison !== KPIComparison.DISCOUNT_LOWER
    ) {
      return '—';
    }
    const subRowsValues: number[] = [];
    row.subRows.forEach((subRow) => {
      const comparedItem = evolutionInsights?.campaignItemsWithEvolution.find(
        (item) => item.dealItemId === subRow.original.id
      );
      if (
        comparedItem?.chooseDiscount !== undefined &&
        comparedItem.chooseDiscount !== null
      ) {
        subRowsValues.push(comparedItem.chooseDiscount * 100);
      }
    });
    if (subRowsValues.length === 0) return '—';
    const min = Math.min(...subRowsValues);
    const max = Math.max(...subRowsValues);
    return min === max
      ? `${formatDecimal(max)}%`
      : `${formatDecimal(min)}% - ${formatDecimal(max)}%`;
  };

  const emptyErrorState = getTableCellErrorState({
    row,
    field: 'chooseDiscount',
    errorType: 'empty',
  });
  const hasRuleError = useCellValidationRuleError(row, 'chooseDiscount');
  const errorState = {
    hasError: emptyErrorState.hasError || hasRuleError,
    isEmpty: emptyErrorState.isEmpty,
    errorMessage: emptyErrorState.errorMessage,
  };

  const getPreviousItem = () => {
    if (kpiComparison === KPIComparison.DISCOUNT_HIGHER) {
      return evolutionInsights?.campaignItemsWithEvolution.find(
        (item) => item.dealItemId === row.original.id
      )?.chooseDiscount;
    }
    if (kpiComparison === KPIComparison.DISCOUNT_LOWER) {
      return evolutionInsights?.campaignItemsWithEvolution.find(
        (item) => item.dealItemId === row.original.id
      )?.chooseDiscount;
    }
  };

  const value = getValue();
  const hasComparison =
    kpiComparison === KPIComparison.DISCOUNT_HIGHER ||
    kpiComparison === KPIComparison.DISCOUNT_LOWER;

  return (
    <TableCellWithRecentlyModified
      productId={row.original.id}
      field="chooseDiscount"
      isParent={isParent}
    >
      {(isRecentlyModified) => (
        <div className="flex h-full w-full flex-row">
          <div className={cn('h-full', hasComparison ? 'w-[160px]' : 'w-full')}>
            {isEditing ? (
              <TablePercentageInput
                value={row.original.activeVersion.chooseDiscount ?? null}
                onChange={async (value) => {
                  await handleCellEdit(
                    row.original.activeVersion.id,
                    'chooseDiscount',
                    value,
                    !isParent
                  );
                }}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                disabled={isOfferLocked}
                autoFocus={isEditing}
                hasError={errorState.hasError}
                clampRange={{ min: 0, max: 100 }}
              />
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="h-full w-full"
                    onClick={() =>
                      !isParent &&
                      handleCellClick(
                        row.original.activeVersion.id,
                        'chooseDiscount'
                      )
                    }
                  >
                    <TablePercentageDisplay
                      value={
                        errorState.isEmpty
                          ? null
                          : typeof value === 'number' && !isParent
                            ? `${formatDecimal(value)}%${
                                hasComparison && getPreviousItem()
                                  ? ` (${formatDecimal(value - (getPreviousItem() || 0) * 100)}pt)`
                                  : ''
                              }`
                            : isParent
                              ? getParentValue()
                              : null
                      }
                      isEmpty={errorState.isEmpty}
                      hasError={errorState.hasError}
                      errorMessage={errorState.errorMessage}
                      isRecentlyModified={isRecentlyModified}
                      tooltipContent={
                        !isParent &&
                        user?.role &&
                        hasPreviousVersion(
                          row.original,
                          'chooseDiscount',
                          user?.role
                        ) ? (
                          <div>
                            <p className="text-sm font-semibold">
                              {t('kpi.choose_discount_modified')}
                            </p>
                            <p>
                              <span className="text-gray-500">
                                {t('general.previous')} :
                              </span>{' '}
                              <span>
                                {formatDecimal(
                                  getMyLastValue(
                                    row.original,
                                    'chooseDiscount',
                                    user?.role
                                  )
                                )}{' '}
                                pts
                              </span>
                            </p>
                            <p>
                              <span className="text-gray-500">
                                {t('general.current')} :
                              </span>{' '}
                              <span>
                                {formatDecimal(
                                  getCurrentValue(
                                    row.original,
                                    'chooseDiscount'
                                  )
                                )}{' '}
                                pts
                              </span>
                            </p>
                            <p className="font-semibold">
                              <span className="text-gray-500">
                                {t('general.gap')} :
                              </span>{' '}
                              <span
                                className={cn({
                                  'text-red-700':
                                    getDeltaValue(
                                      row.original,
                                      'chooseDiscount',
                                      user?.role
                                    ) < 0,
                                  'text-green-700':
                                    getDeltaValue(
                                      row.original,
                                      'chooseDiscount',
                                      user?.role
                                    ) > 0,
                                })}
                              >
                                {formatDecimal(
                                  getDeltaValue(
                                    row.original,
                                    'chooseDiscount',
                                    user?.role
                                  )
                                )}{' '}
                                pts
                              </span>
                            </p>
                          </div>
                        ) : undefined
                      }
                      isEditable={!isOfferLocked}
                      isParent={isParent}
                    >
                      {!isParent &&
                        user?.role &&
                        hasPreviousVersion(
                          row.original,
                          'chooseDiscount',
                          user?.role
                        ) &&
                        getDeltaSign(
                          row.original,
                          'chooseDiscount',
                          user?.role
                        ) > 0 && (
                          <TrendingUp className="absolute right-[-11px] top-[-11px] h-4 w-4 rounded-full bg-green-100 p-[2px] text-green-700" />
                        )}
                      {!isParent &&
                        user?.role &&
                        hasPreviousVersion(
                          row.original,
                          'chooseDiscount',
                          user?.role
                        ) &&
                        getDeltaSign(
                          row.original,
                          'chooseDiscount',
                          user?.role
                        ) < 0 && (
                          <TrendingDown className="absolute right-[-11px] top-[-11px] h-4 w-4 rounded-full bg-red-100 p-[2px] text-red-700" />
                        )}
                    </TablePercentageDisplay>
                  </div>
                </TooltipTrigger>
              </Tooltip>
            )}
          </div>
          {hasComparison && (
            <div
              className={cn(
                'flex-1 text-right',
                (isParent || isOfferLocked) && 'opacity-50',
                isParent || !isOfferLocked
                  ? 'cursor-pointer'
                  : 'cursor-not-allowed'
              )}
              onClick={() =>
                !isParent &&
                handleCellClick(row.original.activeVersion.id, 'chooseDiscount')
              }
            >
              <div
                className={`flex h-full w-full items-center justify-end px-2 text-sm`}
              >
                <span>
                  {!isParent
                    ? `${getPreviousItem() ? formatDecimal(getPreviousItem()! * 100) + '%' : null}`
                    : getParentComparedValue()}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </TableCellWithRecentlyModified>
  );
}

const useColumnDefChooseDiscount = ({
  handleCellClick,
  handleCellEdit,
  handleKeyDown,
  handleBlur,
}: ColumnProps): ExtendedColumnDef<DealItemWithVersions> => {
  const { t } = useTranslation();
  const kpiComparison = useAtomValue(kpiComparisonState);
  const { setColumnSort } = useSetColumnSort();

  const accessorKey: keyof DealItem = 'chooseDiscount';

  return useMemo(
    () => ({
      accessorKey: accessorKey,
      name: 'chooseDiscount',
      accessorFn: (row) => row.activeVersion?.chooseDiscount ?? 0,
      aggregationFn: 'mean',
      sortingFn: 'basic',
      header: () => (
        <div className="flex w-full cursor-pointer flex-row font-semibold">
          <div className="flex w-[160px] items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <InfoIcon className="h-4 w-4" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('kpi.choose_discount_tooltip')}</p>
              </TooltipContent>
            </Tooltip>{' '}
            <div
              className="flex flex-1 items-center gap-2"
              onClick={() => setColumnSort(accessorKey)}
            >
              {t('kpi.choose_discount')}
              <SortIcon pointer="chooseDiscount" />
            </div>
          </div>
          {(kpiComparison === KPIComparison.DISCOUNT_HIGHER ||
            kpiComparison === KPIComparison.DISCOUNT_LOWER) && (
            <div
              className="flex w-full flex-1 items-center justify-end gap-2 text-right"
              onClick={() => setColumnSort(accessorKey)}
            >
              {t('kpi.choose_discount_compared')}
              <SortIcon pointer="chooseDiscount" />
            </div>
          )}
        </div>
      ),
      cell: ({ row }) => (
        <ChooseDiscountCellContent
          row={row}
          handleCellClick={handleCellClick}
          handleCellEdit={handleCellEdit}
          handleKeyDown={handleKeyDown}
          handleBlur={handleBlur}
        />
      ),
      size:
        kpiComparison === KPIComparison.DISCOUNT_HIGHER ||
        kpiComparison === KPIComparison.DISCOUNT_LOWER
          ? 340
          : 180,
    }),
    [
      t,
      kpiComparison,
      setColumnSort,
      handleCellClick,
      handleCellEdit,
      handleKeyDown,
      handleBlur,
    ]
  );
};

export default useColumnDefChooseDiscount;
